/* eslint-disable */
import { Table, Text, VStack } from '@chakra-ui/react';
import { DaysOfWeek, HappyHourDeliveryType, HappyHourDiscountType } from '@client/graphql/types/graphql';
import { useTranslate } from '@tolgee/react';
import { capitalize } from 'lodash';
import DiscountView from '../util/discount-view';
import HappyHourUpdateModal from './happy-hour-update-modal';

interface TimeRule {
  __typename: string;
  daysOfWeek?: DaysOfWeek[];
  isAllDays?: boolean;
  start?: string;
  end?: string;
}

interface HappyHour {
  id: string;
  timeRule: TimeRule;
  deliveryType: HappyHourDeliveryType[];
  discountAmount: number;
  discountType: HappyHourDiscountType;
}

interface HappyHourTableProps {
  data: any | null | undefined;
}

export default function HappyHourTable({ data }: HappyHourTableProps) {
  const { t } = useTranslate();

  if (!data?.getHappyHours) {
    return <Text>{t('happy-hour.alert.error', 'Something went wrong with happy hour data')}</Text>;
  }

  const rows = data.getHappyHours.map((item: HappyHour) => (
    <Table.Row key={item.id}>
      {/* Desktop View */}
      <Table.Cell display={{ base: 'none', md: 'table-cell' }}>
        {item.timeRule.__typename === 'MenuSectionRangeTimeRule'
          ? item.timeRule.daysOfWeek?.map((day: string) => capitalize(day).slice(0, 3)).join(', ')
          : '-'}
      </Table.Cell>
      <Table.Cell display={{ base: 'none', md: 'table-cell' }}>
        {item.timeRule.__typename === 'MenuSectionRangeTimeRule' ? (
          item.timeRule.isAllDays ? (
            t('happy-hour.isAllDays', 'All days')
          ) : (
            <>
              {item.timeRule.start} - {item.timeRule.end}
            </>
          )
        ) : (
          '-'
        )}
      </Table.Cell>
      <Table.Cell display={{ base: 'none', md: 'table-cell' }}>
        {item.deliveryType.map((type: string) => t(type, capitalize(type))).join(', ')}
      </Table.Cell>
      <Table.Cell display={{ base: 'none', md: 'table-cell' }}>
        <DiscountView discountAmount={item.discountAmount} discountType={item.discountType} />
      </Table.Cell>

      {/* Mobile View */}
      <Table.Cell display={{ base: 'table-cell', md: 'none' }}>
        <VStack align="start" gap={1}>
          <Text>
            {item.timeRule.__typename === 'MenuSectionRangeTimeRule'
              ? item.timeRule.daysOfWeek?.map((day: string) => capitalize(day).slice(0, 3)).join(', ')
              : '-'}
          </Text>
          <Text>
            {item.timeRule.__typename === 'MenuSectionRangeTimeRule' ? (
              item.timeRule.isAllDays ? (
                t('happy-hour.isAllDays', 'All days')
              ) : (
                <>
                  {item.timeRule.start} - {item.timeRule.end}
                </>
              )
            ) : (
              '-'
            )}
          </Text>
        </VStack>
      </Table.Cell>

      <Table.Cell display={{ base: 'table-cell', md: 'none' }}>
        <VStack align="start" gap={1}>
          <Text>{item.deliveryType.map((type: string) => capitalize(type)).join(', ')}</Text>
          <DiscountView discountAmount={item.discountAmount} discountType={item.discountType} />
        </VStack>
      </Table.Cell>

      {/* Edit button - visible on both views */}
      <Table.Cell>
        <HappyHourUpdateModal id={item.id} />
      </Table.Cell>
    </Table.Row>
  ));

  return (
    <>
      <Table.Root size="lg" interactive>
        <Table.Header>
          <Table.Row bgColor={'bg.subtle'}>
            {/* Desktop Headers */}
            <Table.ColumnHeader fontWeight={600} display={{ base: 'none', md: 'table-cell' }}>
              {t('happy-hour.table.end-time', 'Day of week')}
            </Table.ColumnHeader>
            <Table.ColumnHeader fontWeight={600} display={{ base: 'none', md: 'table-cell' }}>
              {t('happy-hour.table.start-time', 'Hour range')}
            </Table.ColumnHeader>
            <Table.ColumnHeader fontWeight={600} display={{ base: 'none', md: 'table-cell' }}>
              {t('happy-hour.table.delivery-type', 'Delivery Type')}
            </Table.ColumnHeader>
            <Table.ColumnHeader fontWeight={600} display={{ base: 'none', md: 'table-cell' }}>
              {t('happy-hour.table.discount', 'Discount')}
            </Table.ColumnHeader>

            {/* Mobile Headers */}
            <Table.ColumnHeader fontWeight={600} display={{ base: 'table-cell', md: 'none' }}>
              {t('happy-hour.table.time-info', 'Time Information')}
            </Table.ColumnHeader>
            <Table.ColumnHeader fontWeight={600} display={{ base: 'table-cell', md: 'none' }}>
              {t('happy-hour.table.delivery-discount', 'Delivery & Discount')}
            </Table.ColumnHeader>

            {/* Edit column - visible on both views */}
            <Table.ColumnHeader fontWeight={600} width="100px">
              {t('common.button.edit', 'Edit')}
            </Table.ColumnHeader>
          </Table.Row>
        </Table.Header>

        <Table.Body>{rows}</Table.Body>
      </Table.Root>
    </>
  );
}
